import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, Calendar, CheckSquare, Users, Folder, AlertTriangle, X, CheckCheck, Archive } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = {
    calendar: Calendar,
    task: CheckSquare,
    engineer: Users,
    project: Folder,
    warning: AlertTriangle,
};

function relativeTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function bucketOf(iso) {
    if (!iso) return "earlier";
    const d = new Date(iso);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (d >= today) return "today";
    const yesterday = new Date(today.getTime() - 86400 * 1000);
    if (d >= yesterday) return "yesterday";
    return "earlier";
}

/**
 * NotificationBell — bell icon with unread badge + slide-out Notification Centre.
 *
 * The drawer is rendered via `createPortal` into `document.body` so it always
 * sits above any page-level stacking context (sticky sidebars, hero banners,
 * calendars, modals lower in the tree). It anchors to the right edge, starts
 * beneath the mobile top bar (`h-14` on <sm) or at `top-0` on desktop (where
 * the shell uses a vertical sidebar rather than a horizontal header), and
 * has its own vertical scroll region.
 *
 * Keyboard: Escape closes, focus is trapped inside the drawer while open,
 * and returns to the bell on close.
 *
 * Poll for unread count every 60s so the badge stays roughly live.
 */
export default function NotificationBell({ testidPrefix = "notif" }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const [tab, setTab] = useState("unread"); // unread | all | archived
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const bellRef = useRef(null);
    const drawerRef = useRef(null);

    const refreshCount = useCallback(async () => {
        try { setCount(await api.unreadCount()); }
        catch { /* ignore */ }
    }, []);

    useEffect(() => {
        refreshCount();
        const id = setInterval(refreshCount, 60000);
        return () => clearInterval(id);
    }, [refreshCount]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await api.listNotifications(tab);
            setRows(list);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load notifications");
        } finally { setLoading(false); }
    }, [tab]);

    useEffect(() => { if (open) load(); }, [open, load]);

    // Escape + focus trap + focus return + body scroll lock while drawer open
    useEffect(() => {
        if (!open) return;

        const previousActive = document.activeElement;
        const previousBodyOverflow = document.body.style.overflow;
        // Snapshot the bell element at effect-run time so cleanup returns
        // focus to the exact node that opened the drawer (react-hooks/
        // exhaustive-deps: ref values captured for cleanup MUST NOT be
        // read from `.current` inside the returned function).
        const bellEl = bellRef.current;
        document.body.style.overflow = "hidden";

        // Move focus into the drawer
        const focusFirst = () => {
            const root = drawerRef.current;
            if (!root) return;
            const focusable = root.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            focusable?.focus();
        };
        const t = setTimeout(focusFirst, 30);

        function onKey(e) {
            if (e.key === "Escape") {
                e.stopPropagation();
                setOpen(false);
                return;
            }
            if (e.key !== "Tab") return;
            // Focus trap
            const root = drawerRef.current;
            if (!root) return;
            const items = Array.from(root.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )).filter((el) => !el.hasAttribute("hidden") && el.offsetParent !== null);
            if (!items.length) return;
            const first = items[0], last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault(); last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault(); first.focus();
            }
        }
        document.addEventListener("keydown", onKey, true);

        return () => {
            clearTimeout(t);
            document.removeEventListener("keydown", onKey, true);
            document.body.style.overflow = previousBodyOverflow;
            if (previousActive && typeof previousActive.focus === "function") {
                // Return focus to the bell that opened this drawer
                if (bellEl) bellEl.focus();
                else previousActive.focus();
            }
        };
    }, [open]);

    async function onClickRow(row) {
        try {
            if (row.status !== "read") {
                await api.markNotificationRead(row.id);
                setCount((c) => Math.max(0, c - 1));
                setRows((r) => r.map((x) => (x.id === row.id ? { ...x, status: "read" } : x)));
            }
        } catch { /* best-effort */ }

        setOpen(false);
        // DEV-010D · Canonical deep-link resolution.
        // Business events persist their actionable destination on
        // `payload.link` — the bell prefers that so Site Survey / future
        // survey events land on the correct surface without special-casing
        // event keys in the client.
        const link = row?.payload?.link;
        if (typeof link === "string" && link.startsWith("/")) {
            navigate(link);
            return;
        }
        // Legacy fallbacks — kept for task/appointment/project events.
        if (row.task_id) navigate(`/installer/tasks?scope=all`);
        else if (row.appointment_id) navigate("/installer/calendar");
        else if (row.project_id) navigate(`/installer/project/${row.project_id}`);
    }

    async function markAll() {
        try {
            await api.markAllNotificationsRead();
            toast.success("All marked read");
            setCount(0);
            await load();
        } catch (e) { toast.error("Failed"); }
    }

    async function archive(id) {
        try {
            await api.archiveNotification(id);
            setRows((r) => r.filter((x) => x.id !== id));
        } catch { toast.error("Failed to archive"); }
    }

    const grouped = useMemo(() => {
        const g = { today: [], yesterday: [], earlier: [] };
        rows.forEach((r) => g[bucketOf(r.created_at)].push(r));
        return g;
    }, [rows]);

    // ─── Drawer overlay (portal target: document.body) ─────────────────────
    // z-[70] sits above:
    //   • mobile top bar     (z-30 in the shells)
    //   • mobile menu drawer (z-40 in the shells)
    //   • sticky sidebar stacking contexts
    //   • hero banners with negative-z decorations
    // and below Sonner toasts which default to z-999999.
    const drawer = open ? (
        <div
            className="fixed inset-x-0 top-14 sm:top-0 bottom-0 z-[70]"
            role="presentation"
            data-testid={`${testidPrefix}-drawer`}
        >
            {/* Scrim */}
            <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setOpen(false)}
                data-testid={`${testidPrefix}-drawer-scrim`}
                aria-hidden="true"
            />
            {/* Drawer */}
            <aside
                ref={drawerRef}
                className="absolute right-0 top-0 bottom-0 w-full sm:max-w-md bg-white shadow-2xl flex flex-col animate-[slide-in-right_0.22s_ease-out]"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${testidPrefix}-drawer-title`}
                data-testid={`${testidPrefix}-drawer-panel`}
            >
                <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center gap-3">
                    <h3
                        id={`${testidPrefix}-drawer-title`}
                        className="text-lg font-semibold text-[#131B2F] flex-1"
                    >
                        Notifications
                    </h3>
                    <button
                        onClick={markAll}
                        data-testid={`${testidPrefix}-mark-all`}
                        title="Mark all read"
                        className="inline-flex items-center gap-1 text-sm text-[#3E9E2E] hover:underline"
                    >
                        <CheckCheck size={14} strokeWidth={1.75} /> Read all
                    </button>
                    <button
                        onClick={() => setOpen(false)}
                        data-testid={`${testidPrefix}-close`}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F5F5F7] text-[#4B5565]"
                        aria-label="Close notifications"
                    >
                        <X size={18} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="px-5 pt-3 pb-2 flex items-center gap-1" role="tablist" aria-label="Notification filters">
                    {[
                        { key: "unread", label: "Unread" },
                        { key: "all", label: "All" },
                        { key: "archived", label: "Archived" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            role="tab"
                            aria-selected={tab === t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`${testidPrefix}-tab-${t.key}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium ${tab === t.key ? "bg-[#131B2F] text-white" : "bg-[#F5F5F7] text-[#131B2F] hover:bg-[#E5E7EB]"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div
                    className="flex-1 overflow-y-auto px-1 py-2"
                    role="region"
                    aria-label="Notification list"
                    data-testid={`${testidPrefix}-list`}
                >
                    {loading ? (
                        <div className="text-sm text-[#4B5565] px-4 py-6">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="text-sm text-[#4B5565] px-4 py-10 text-center" data-testid={`${testidPrefix}-empty`}>
                            {tab === "unread" ? "You're all caught up ✨" : "Nothing here"}
                        </div>
                    ) : (
                        ["today", "yesterday", "earlier"].map((bucket) =>
                            grouped[bucket].length ? (
                                <section key={bucket} className="mb-2" aria-label={bucket}>
                                    <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.14em] text-[#4B5565]">
                                        {bucket === "today" ? "Today" : bucket === "yesterday" ? "Yesterday" : "Earlier"}
                                    </div>
                                    {grouped[bucket].map((r) => {
                                        const Icon = ICONS[r.icon] || Bell;
                                        const unread = r.status !== "read";
                                        return (
                                            <div
                                                key={r.id}
                                                className={`group flex items-start gap-3 px-4 py-3 hover:bg-[#F5F5F7] transition-colors ${unread ? "bg-[#3E9E2E]/5" : ""}`}
                                                data-testid={`${testidPrefix}-row-${r.id}`}
                                            >
                                                <button
                                                    onClick={() => onClickRow(r)}
                                                    className="flex-1 min-w-0 text-left flex items-start gap-3"
                                                    data-testid={`${testidPrefix}-open-${r.id}`}
                                                >
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${r.critical ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#3E9E2E]/10 text-[#3E9E2E]"}`}>
                                                        <Icon size={14} strokeWidth={1.75} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className={`text-sm truncate ${unread ? "font-semibold text-[#131B2F]" : "text-[#131B2F]"}`}>
                                                            {r.in_app_title || r.summary}
                                                        </div>
                                                        <div className="text-xs text-[#4B5565] truncate">
                                                            {r.in_app_body || ""}
                                                        </div>
                                                        <div className="text-[10px] text-[#4B5565] mt-0.5">{relativeTime(r.created_at)}</div>
                                                    </div>
                                                    {unread && <span className="mt-1 w-2 h-2 rounded-full bg-[#3E9E2E]" aria-label="Unread" />}
                                                </button>
                                                <button
                                                    onClick={() => archive(r.id)}
                                                    className="opacity-100 sm:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity w-8 h-8 flex items-center justify-center rounded-full hover:bg-white text-[#4B5565]"
                                                    aria-label={`Archive notification: ${r.in_app_title || r.summary}`}
                                                    data-testid={`${testidPrefix}-archive-${r.id}`}
                                                >
                                                    <Archive size={14} strokeWidth={1.75} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </section>
                            ) : null
                        )
                    )}
                </div>
            </aside>
        </div>
    ) : null;

    return (
        <>
            <button
                ref={bellRef}
                onClick={() => setOpen(true)}
                data-testid={`${testidPrefix}-bell`}
                className="relative w-10 h-10 flex items-center justify-center rounded-full text-white/85 hover:text-white hover:bg-white/[0.08] transition-colors"
                aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ""}`}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <Bell size={16} strokeWidth={1.75} />
                {count > 0 && (
                    <span
                        className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DA3535] text-white text-[10px] font-semibold flex items-center justify-center"
                        data-testid={`${testidPrefix}-bell-count`}
                    >
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>
            {drawer && createPortal(drawer, document.body)}
        </>
    );
}
